import { t, Trans } from '@lingui/macro'
import Tooltip from '@mui/material/Tooltip'
import { useSyncPatientChangesToSnowflake } from '@om1/aspen-api'
import { useEffect, useState } from 'react'

const SyncPatientChangesToSnowflakeButton = ({
    patientId,
    hasGreenlightId,
    onSuccess
}: {
    patientId: string
    hasGreenlightId?: boolean
    onSuccess?: () => void
}) => {
    const { taskStatus, loading, start } = useSyncPatientChangesToSnowflake(patientId)
    const [isButtonLoading, setButtonLoading] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    useEffect(() => {
        if (taskStatus?.status === 'SUCCESS') {
            onSuccess?.()
        }
    }, [onSuccess, taskStatus?.status])

    const handleButtonClick = async () => {
        setButtonLoading(true)
        try {
            await start(patientId)
        } catch (err) {
            // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
            console.error('Error triggering workflow:', err)
        } finally {
            setButtonLoading(false)
        }
    }

    const renderTaskStatus = () => {
        if (!taskStatus) return null

        switch (taskStatus.status) {
            case 'PENDING':
                return (
                    <p>
                        <Trans>Task is pending...</Trans>
                    </p>
                )
            case 'STARTED':
                return (
                    <p>
                        <Trans>Task has started...</Trans>
                    </p>
                )
            case 'RETRY':
                return (
                    <p>
                        <Trans>Task is retrying...</Trans>
                    </p>
                )
            case 'REVOKED':
                return (
                    <p>
                        <Trans>Task has been revoked.</Trans>
                    </p>
                )
            case 'FAILURE':
                return (
                    <p>
                        <Trans>Task failed. Please try again.</Trans>
                    </p>
                )
            case 'SUCCESS':
                return (
                    <p>
                        <Trans>Task completed successfully!</Trans>
                    </p>
                )
            default:
                return (
                    <p>
                        <Trans>Unknown task status.</Trans>
                    </p>
                )
        }
    }

    return (
        <div>
            <Tooltip title={!hasGreenlightId ? t`Patient is not associated with Greenlight` : ''} arrow>
                <span>
                    <button
                        onClick={handleButtonClick}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        disabled={
                            isButtonLoading ||
                            loading ||
                            !hasGreenlightId ||
                            (taskStatus?.status !== undefined && taskStatus?.status !== null && taskStatus?.status !== 'FAILURE')
                        }
                        className={`
                            flex items-center justify-center gap-1.5
                            px-3 py-1.5
                            rounded-md
                            transition-colors
                            text-sm font-medium
                            min-w-[140px]
                            shadow-sm
                            h-[36px]
                            ${
                                loading || !hasGreenlightId || (taskStatus?.status !== undefined && taskStatus?.status !== null)
                                    ? taskStatus?.status === 'SUCCESS'
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : taskStatus?.status === 'FAILURE'
                                        ? 'bg-red-400 text-white hover:bg-red-500'
                                        : taskStatus?.status === 'STARTED' || taskStatus?.status === 'PENDING' || loading
                                        ? 'bg-orange-400 text-white hover:bg-orange-500'
                                        : 'bg-gray-400 text-white'
                                    : 'bg-primary-main text-white hover:bg-primary-dark'
                            }
                            ${isButtonLoading || loading || !hasGreenlightId ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                    >
                        <span>
                            {isButtonLoading || loading
                                ? t`Loading...`
                                : taskStatus?.status !== undefined && taskStatus?.status !== null
                                ? taskStatus.status === 'FAILURE' && isHovered
                                    ? t`Retry`
                                    : renderTaskStatus()
                                : t`Sync Patient Changes to Snowflake`}
                        </span>
                    </button>
                </span>
            </Tooltip>
        </div>
    )
}

export default SyncPatientChangesToSnowflakeButton
